import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BookOpen,
  Clock,
  FileText,
  Link,
  Loader2,
  Search,
  Upload,
  Video,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useKnowledgeAssets } from '@/hooks/useKnowledgeAssets';
import contentLibraryService, {
  type OrganizationSegmentCatalogItem,
} from '@/lib/api/contentLibrary';
import {
  detectKnowledgeAssetFileType,
  getKnowledgeAssetTypeLabel,
  isDocumentKnowledgeAssetType,
  type KnowledgeAsset,
} from '@/lib/api/knowledgeAssets';
import type {
  KGSummaryResponse,
  KGTrainingSourceRef,
} from '@/lib/api/knowledgeGaps';
import { cn } from '@/lib/utils';

type StartMode = 'none' | 'source';
type SourceFilter = 'all' | 'documents' | 'segments';

const ACCEPTED_DOCUMENT_UPLOADS =
  '.pdf,.md,.markdown,.txt,application/pdf,text/markdown,text/plain';

function assetTitle(asset: KnowledgeAsset): string {
  return asset.title || asset.filename || asset.url || 'Untitled source';
}

function formatSeconds(seconds: number | null | undefined): string {
  if (seconds == null || Number.isNaN(seconds)) return '0:00';
  const totalSeconds = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(totalSeconds / 60);
  const remainder = totalSeconds % 60;
  return `${minutes}:${String(remainder).padStart(2, '0')}`;
}

function segmentHasContext(segment: OrganizationSegmentCatalogItem): boolean {
  return [
    segment.summary,
    segment.description,
    segment.speech_transcript,
    segment.visual_description,
    segment.scene_text,
    segment.ai_context,
  ].some((value) => String(value || '').trim().length > 0);
}

function sourceKey(ref: KGTrainingSourceRef): string {
  return `${ref.source_type}:${ref.id}`;
}

function sameSource(
  left: KGTrainingSourceRef | null,
  right: KGTrainingSourceRef,
): boolean {
  return Boolean(left && left.source_type === right.source_type && left.id === right.id);
}

interface SourceUnit {
  key: string;
  ref: KGTrainingSourceRef;
  kind: 'document' | 'segment';
  title: string;
  subtitle: string;
  preview?: string;
  badge: string;
}

export function TrainingAssetSetupModal({
  open,
  onOpenChange,
  orgId,
  summary,
  isStarting,
  onStart,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string;
  summary: KGSummaryResponse;
  isStarting: boolean;
  onStart: (sources?: KGTrainingSourceRef[]) => void;
}) {
  const { assets, isLoading, uploadFile, isUploading } = useKnowledgeAssets(orgId);
  const [mode, setMode] = useState<StartMode>('none');
  const [selectedSource, setSelectedSource] = useState<KGTrainingSourceRef | null>(null);
  const [uploadedAsset, setUploadedAsset] = useState<KnowledgeAsset | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [search, setSearch] = useState('');

  const {
    data: segmentData,
    isLoading: segmentsLoading,
    error: segmentsError,
  } = useQuery({
    queryKey: ['kg-training-source-segments', orgId],
    queryFn: async () => {
      const response = await contentLibraryService.listOrganizationSegments(orgId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to load video segments');
      }
      return response.data;
    },
    enabled: open && mode === 'source' && Boolean(orgId),
  });

  useEffect(() => {
    if (!open) return;
    setMode('none');
    setSelectedSource(null);
    setUploadedAsset(null);
    setUploadError(null);
    setSourceFilter('all');
    setSearch('');
  }, [open, summary.id]);

  const completedDocumentAssets = useMemo(() => {
    const byId = new Map<string, KnowledgeAsset>();
    assets
      .filter(
        (asset) =>
          asset.processing_status === 'completed' &&
          (asset.asset_type === 'url' || isDocumentKnowledgeAssetType(asset.asset_type)),
      )
      .forEach((asset) => byId.set(asset.id, asset));
    if (
      uploadedAsset?.processing_status === 'completed' &&
      (uploadedAsset.asset_type === 'url' ||
        isDocumentKnowledgeAssetType(uploadedAsset.asset_type))
    ) {
      byId.set(uploadedAsset.id, uploadedAsset);
    }
    return Array.from(byId.values());
  }, [assets, uploadedAsset]);

  const usableSegments = useMemo(
    () =>
      (segmentData?.segments ?? []).filter(
        (segment) =>
          segment.video_status === 'ready' &&
          segment.catalog_status !== 'ignored' &&
          segmentHasContext(segment),
      ),
    [segmentData?.segments],
  );

  const sourceUnits = useMemo<SourceUnit[]>(() => {
    const documentUnits = completedDocumentAssets.map((asset): SourceUnit => ({
      key: `knowledge_asset:${asset.id}`,
      ref: { source_type: 'knowledge_asset', id: asset.id },
      kind: 'document',
      title: assetTitle(asset),
      subtitle: asset.url || asset.filename || getKnowledgeAssetTypeLabel(asset.asset_type, asset.url_type),
      preview: asset.summary || undefined,
      badge: getKnowledgeAssetTypeLabel(asset.asset_type, asset.url_type),
    }));

    const segmentUnits = usableSegments.map((segment): SourceUnit => {
      const videoTitle =
        segment.video_title || segment.video_filename || 'Untitled video';
      const timestamp = `${formatSeconds(segment.start_time)}-${formatSeconds(segment.end_time)}`;
      return {
        key: `video_segment:${segment.segment_id}`,
        ref: { source_type: 'video_segment', id: segment.segment_id },
        kind: 'segment',
        title: segment.label || segment.summary || segment.description || 'Untitled segment',
        subtitle: `${videoTitle} - ${timestamp}`,
        preview:
          segment.summary ||
          segment.description ||
          segment.speech_transcript ||
          segment.visual_description ||
          undefined,
        badge: 'Video segment',
      };
    });

    return [...documentUnits, ...segmentUnits];
  }, [completedDocumentAssets, usableSegments]);

  const filteredSources = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return sourceUnits.filter((unit) => {
      if (sourceFilter === 'documents' && unit.kind !== 'document') return false;
      if (sourceFilter === 'segments' && unit.kind !== 'segment') return false;
      if (!normalizedSearch) return true;
      return [unit.title, unit.subtitle, unit.preview, unit.badge]
        .some((value) => String(value || '').toLowerCase().includes(normalizedSearch));
    });
  }, [search, sourceFilter, sourceUnits]);

  const selectedUnit = useMemo(
    () =>
      selectedSource
        ? sourceUnits.find((unit) => unit.key === sourceKey(selectedSource)) ?? null
        : null,
    [selectedSource, sourceUnits],
  );

  const latestUploadedAsset = uploadedAsset
    ? assets.find((asset) => asset.id === uploadedAsset.id) ?? uploadedAsset
    : null;
  const selectedUploadedAsset =
    latestUploadedAsset && selectedSource?.id === latestUploadedAsset.id
      ? latestUploadedAsset
      : null;
  const waitingForUploadedAsset =
    selectedUploadedAsset !== null &&
    selectedUploadedAsset.processing_status !== 'completed';
  const sourceIsReady = mode === 'source' && selectedUnit !== null;
  const startDisabled =
    isStarting || isUploading || (mode === 'source' && !sourceIsReady);
  const showSourceLoading = isLoading || segmentsLoading;

  const handleFileUpload = async (file: File | null) => {
    if (!file) return;
    setUploadError(null);

    const detectedType = detectKnowledgeAssetFileType(file);
    if (!detectedType || detectedType === 'video') {
      setUploadError(
        'Upload a PDF, Markdown, or text file. For videos, select a processed video segment.',
      );
      return;
    }

    try {
      const asset = await uploadFile(file, file.name);
      setUploadedAsset(asset);
      setSelectedSource({ source_type: 'knowledge_asset', id: asset.id });
      setMode('source');
      setSourceFilter('documents');
    } catch (error) {
      setUploadError(
        error instanceof Error
          ? error.message
          : 'We could not upload this source. Try again.',
      );
    }
  };

  const start = () => {
    if (mode === 'none') {
      onStart(undefined);
      return;
    }
    if (!selectedSource || !sourceIsReady) return;
    onStart([selectedSource]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="h-4 w-4" />
            Start training session
          </DialogTitle>
          <DialogDescription>
            Start the interview now, or include one focused source as context for this gap.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto space-y-5 pr-1">
          <RadioGroup
            value={mode}
            onValueChange={(value) => setMode(value as StartMode)}
            className="gap-3"
          >
            <label
              className={cn(
                'flex cursor-pointer items-start gap-3 rounded-md border border-border p-3 text-sm',
                mode === 'none' && 'border-primary bg-primary/5',
              )}
            >
              <RadioGroupItem value="none" className="mt-0.5" />
              <span className="space-y-0.5">
                <span className="block font-medium">Start without a source</span>
                <span className="block text-muted-foreground">
                  Use the gap details and conversation evidence already attached to this session.
                </span>
              </span>
            </label>

            <label
              className={cn(
                'flex cursor-pointer items-start gap-3 rounded-md border border-border p-3 text-sm',
                mode === 'source' && 'border-primary bg-primary/5',
              )}
            >
              <RadioGroupItem value="source" className="mt-0.5" />
              <span className="space-y-0.5">
                <span className="block font-medium">Include a knowledge source</span>
                <span className="block text-muted-foreground">
                  Select a document, URL, or processed video segment before starting.
                </span>
              </span>
            </label>
          </RadioGroup>

          {mode === 'source' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium">Existing sources</h4>
                  {showSourceLoading && (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  )}
                </div>

                <div className="flex flex-col gap-2 sm:flex-row">
                  <div className="relative flex-1">
                    <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      placeholder="Search sources"
                      className="pl-8"
                    />
                  </div>
                  <div className="flex rounded-md border border-border p-0.5">
                    {([
                      ['all', 'All'],
                      ['documents', 'Docs'],
                      ['segments', 'Segments'],
                    ] as const).map(([key, label]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setSourceFilter(key)}
                        className={cn(
                          'rounded-sm px-2.5 py-1.5 text-xs font-medium transition-colors',
                          sourceFilter === key
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:text-foreground',
                        )}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {segmentsError instanceof Error && (
                  <p className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                    {segmentsError.message}
                  </p>
                )}

                {filteredSources.length === 0 && !showSourceLoading ? (
                  <p className="rounded-md border border-dashed border-border p-3 text-sm text-muted-foreground">
                    No matching document, URL, or video segment sources are ready yet.
                  </p>
                ) : (
                  <div className="max-h-64 space-y-1 overflow-y-auto rounded-md border border-border p-1">
                    {filteredSources.map((unit) => {
                      const selected = sameSource(selectedSource, unit.ref);
                      const Icon = unit.kind === 'segment'
                        ? Video
                        : unit.badge.startsWith('URL')
                          ? Link
                          : FileText;
                      return (
                        <button
                          key={unit.key}
                          type="button"
                          disabled={isStarting || isUploading}
                          onClick={() => {
                            setSelectedSource(unit.ref);
                            setMode('source');
                          }}
                          className={cn(
                            'flex w-full items-start gap-3 rounded-sm px-3 py-2 text-left text-sm transition-colors',
                            selected ? 'bg-primary/10 text-primary' : 'hover:bg-muted',
                            (isStarting || isUploading) && 'cursor-not-allowed opacity-60',
                          )}
                        >
                          <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate font-medium">
                              {unit.title}
                            </span>
                            <span className="block truncate text-xs text-muted-foreground">
                              {unit.subtitle}
                            </span>
                            {unit.preview && (
                              <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                                {unit.preview}
                              </span>
                            )}
                          </span>
                          <Badge variant="secondary" className="shrink-0 text-[10px]">
                            {unit.badge}
                          </Badge>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <h4 className="text-sm font-medium">Upload new document</h4>
                <div className="rounded-md border border-dashed border-border p-3">
                  <div className="flex items-center gap-3">
                    <Upload className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <Input
                      type="file"
                      accept={ACCEPTED_DOCUMENT_UPLOADS}
                      disabled={isStarting || isUploading}
                      onChange={(event) => {
                        const file = event.target.files?.[0] ?? null;
                        void handleFileUpload(file);
                        event.target.value = '';
                      }}
                    />
                  </div>
                  {isUploading && (
                    <p className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Uploading and processing document...
                    </p>
                  )}
                  {uploadError && (
                    <p className="mt-2 text-xs text-destructive">{uploadError}</p>
                  )}
                </div>
              </div>

              {waitingForUploadedAsset && selectedUploadedAsset && (
                <p className="flex items-center gap-2 rounded-md border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
                  <Clock className="h-4 w-4 shrink-0" />
                  {assetTitle(selectedUploadedAsset)} is {selectedUploadedAsset.processing_status}.
                  The interview can start once processing completes.
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={isStarting}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button type="button" disabled={startDisabled} onClick={start}>
            {isStarting && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
            Start interview
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
